import * as React from 'react';

import type {FilterObject} from 'nutshell-core/types';

import {getIconDetails, getConnectedIconDetails} from 'shells/connected-forms/helpers';
import {MappingCardStatus} from 'shells/connected-forms/mapping-card-status';

import type {FormFieldType, EntityType} from 'nutshell-graphql-types';
import type {
    WfFormFragment_publishedVersion_fields as Field,
    WfFormFragment_publishedVersion_fields_exampleValues as ExampleValues,
    NextStepsFragment_slackMessage,
    NextStepsFragment_nutshellNotification,
    WfFormFragment as Form,
} from 'nutshell-graphql-types';

import {fieldSortFunction} from '../../core/nutshell-fields/helpers';
import type {FormFieldSchema, FormSchemaProperty} from '../../core/nutshell-fields/types';
import {isDisabled} from '../../forms/form-page/editor/preview/add-card/helpers';
import {FormStatusPrimaryText} from '../../forms/form-page/form-status-primary-text';

import {translateFieldTypeToHumanReadable} from '../connected-forms-list/helpers';
import WF_FORM_FRAGMENT from '../graphql/fragments/wf-form-fragment.graphql';
import {getFormStatus, getStatusSecondaryText} from '../helpers';

import {BASE_COMPANY_FIELD_NAMES, BASE_PERSON_FIELD_NAMES, INPUT_FIELD_TYPES} from './constants';
import './mapping-form.module.css';
import type {SelectedField} from './types';

export type EntityTypeFromApi = 'Contacts' | 'Accounts' | 'Leads';

export function getFieldExamplesString(exampleValues?: ExampleValues | null): string {
    return exampleValues && exampleValues.all
        ? exampleValues.all
              .map((exampleValue) => {
                  // @ts-expect-error
                  return exampleValue.value ? exampleValue.value : '';
              })
              .join(', ')
        : '';
}

export function getFilteredFields(
    formSchema: FormFieldSchema,
    mappedFields: any[],
    type: FormFieldType
): FormSchemaProperty[] {
    const schemaFields = formSchema ? formSchema.properties : undefined;

    let filteredFields = [];

    // Basic text input field
    if (schemaFields && type === 'INPUT') {
        filteredFields = Object.keys(schemaFields)
            .filter(
                (key) =>
                    // don't show fields that aren't for type input
                    // @ts-expect-error
                    INPUT_FIELD_TYPES.includes(schemaFields[key].type) &&
                    // don't show fields that aren't enabled (rare case, but used for migrations)
                    // @ts-expect-error
                    schemaFields[key].isEnabled
            )
            // get array of all schema fields by key
            .map((key) => schemaFields[key])
            // return one field per available entityType for each schema field by key
            // (ex. show 'Address' for company and person)
            .reduce<Array<any>>(function (newFields, field) {
                // @ts-expect-error
                const updatedFields = field.entityTypes.map((entityType) => {
                    const newField = {...field, entityType} as const;

                    return {
                        ...newField,
                        isDisabled: isDisabled(
                            mappedFields.map((mappedField) => ({
                                ...mappedField,
                                // This helper function expects the value in entityName to be called
                                // entityType and the value to be the GraphQL entity typename
                                // ex: 'CONTACTS' vs. 'Contacts'
                                entityType: mappedField.entityName,
                            })),
                            newField as FormSchemaProperty
                        ),
                    };
                });

                return newFields.concat(updatedFields);
            }, [])
            // sort the disabled fields to the bottom of the list, then by type, then by name
            .sort((a, b) => fieldSortFunction(a, b));
    }

    return filteredFields;
}

export function getConnectedFieldContent(
    formSchema: FormFieldSchema,
    selectedField: SelectedField,
    isIgnored: boolean
): React.ReactElement {
    const isConnected = (selectedField.fieldName || selectedField.attributeId) && !isIgnored;
    const iconDetails = isConnected
        ? getConnectedIconDetails()
        : getIconDetails(selectedField.entityName, isIgnored);
    const icon = renderIcon(iconDetails.icon, 16, iconDetails.color);

    return (
        <MappingCardStatus
            icon={icon}
            title={isConnected ? 'Connected' : getConnectedCardStatus(isIgnored)}
        />
    );
}

export function translateEntityToApiEntityType(entityType: EntityTypeFromApi): EntityType {
    switch (entityType) {
        case 'Accounts':
            return 'ACCOUNT';
        case 'Contacts':
            return 'CONTACT';
        case 'Leads':
        default:
            return 'LEAD';
    }
}

export function getConnectedCardStatus(isIgnored: boolean) {
    return isIgnored ? 'Ignored' : 'Needs connecting';
}

export function getFieldStatusTitle(
    formSchema: FormFieldSchema,
    fieldKey?: string | null,
    inputName?: string
): string {
    const schemaFields = formSchema ? formSchema.properties : undefined;

    if (fieldKey && schemaFields && schemaFields[fieldKey]) {
        return schemaFields[fieldKey].label;
    }

    return inputName || '';
}

export function getFieldStatusSubtitle(
    fieldName: string | null | undefined,
    fieldType: FormFieldType,
    isIgnored: boolean
): string {
    if (fieldName) {
        return '';
    }

    if (isIgnored) {
        return 'Ignored';
    }

    return translateFieldTypeToHumanReadable(fieldType) || '';
}

export const getDisabledText = (
    fields: Field[],
    fieldKey: string,
    entityType: EntityTypeFromApi
): string => {
    const hasFirstName = Boolean(fields.find((field) => field.fieldName === 'firstName'));
    const hasLastName = Boolean(fields.find((field) => field.fieldName === 'lastName'));

    if (fieldKey === 'firstName') {
        if (hasFirstName) {
            return 'This field is already in the form';
        } else {
            return 'Name is already in the form';
        }
    }

    if (fieldKey === 'lastName') {
        if (hasLastName) {
            return 'This field is already in the form';
        } else {
            return 'Name is already in the form';
        }
    }

    if (fieldKey === 'name' && entityType === 'Contacts') {
        if (hasFirstName && hasLastName) {
            return 'First name and last name are already in the form';
        }
        if (hasFirstName) {
            return 'First name is already in the form';
        }
        if (hasLastName) {
            return 'Last name is already in the form';
        } else {
            return 'This field is already in the form';
        }
    }

    return 'This field is already in the form';
};

export function renderIcon(icon: React.ComponentType<any>, iconSize: number, iconColor: string) {
    const Icon = icon;

    return <Icon size={iconSize} wrapWithDiv={false} fill={iconColor} color={iconColor} />;
}

// Function to check if a field is a base field for a specific entity, or all entities
export function isBaseField(field: FormSchemaProperty, baseFieldEntity?: EntityType): boolean {
    switch (baseFieldEntity) {
        case 'CONTACT':
            // @ts-expect-error
            return field.entityType === 'Contacts' && BASE_PERSON_FIELD_NAMES.includes(field.name);
        case 'ACCOUNT':
            // @ts-expect-error
            return field.entityType === 'Accounts' && BASE_COMPANY_FIELD_NAMES.includes(field.name);
        case undefined:
            // Check that field is not base field for any entityType
            return (
                // @ts-expect-error
                (field.entityType === 'Contacts' && BASE_PERSON_FIELD_NAMES.includes(field.name)) ||
                (field.entityType === 'Accounts' &&
                    BASE_COMPANY_FIELD_NAMES.includes(
                        // @ts-expect-error
                        field.name as keyof typeof BASE_COMPANY_FIELD_NAMES
                    ))
            );
        case 'LEAD':
        default:
            // Currently, there are no base field types for entityType LEAD
            return false;
    }
}

export function nextStepsCacheUpdate({
    client,
    formId,
    nextStepsKey,
    nextStep,
}: {
    client: any;
    formId: string;
    nextStepsKey: 'slackMessage' | 'nutshellNotification';
    nextStep:
        | NextStepsFragment_slackMessage
        | NextStepsFragment_nutshellNotification
        | null
        | undefined;
}) {
    const formFragment = client.readFragment({
        id: formId,
        fragmentName: 'WfFormFragment',
        fragment: WF_FORM_FRAGMENT,
    });

    if (formFragment) {
        client.writeFragment({
            id: formId,
            fragment: WF_FORM_FRAGMENT,
            fragmentName: 'WfFormFragment',
            data: {
                ...formFragment,
                nextSteps: {
                    ...formFragment.nextSteps,
                    [nextStepsKey]: nextStep,
                },
            },
        });
    }
}

export function getStatusInfo(form: Form, activeTab: string, activeFilters: FilterObject[]) {
    return {
        status: getFormStatus(form.status),
        primaryText: (
            <FormStatusPrimaryText
                numSubmissions={form.numSubmissions}
                activeTab={activeTab}
                activeFilters={activeFilters}
            />
        ),
        secondaryText: (
            <div className='flex mt-4'>
                {getStatusSecondaryText(
                    form.status,
                    form.publishedVersion
                        ? form.publishedVersion.fields.filter(
                              (field) => !field.isIgnored && !field.needsMapping
                          ).length
                        : 0
                )}
            </div>
        ),
    };
}
